import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { ArrowLeft, Boxes, RefreshCw, AlertTriangle, PackageCheck, Trash2 } from "lucide-react";

const STATUS_TABS = [
  { key: "RECEIVED", label: "Im Warehouse", icon: PackageCheck, tone: "bg-green-50 text-green-700" },
  { key: "DAMAGED", label: "Beschädigt", icon: AlertTriangle, tone: "bg-red-50 text-red-700" },
  { key: "DISPOSED_UNSOLD", label: "Entsorgt", icon: Trash2, tone: "bg-[#F5F5F5] text-[#666]" },
];

/**
 * Warehouse-Bestand: physisch vorhandene Rescue-Bags.
 * Wareneingang läuft ausschließlich über Hub-Eingang (Admin-Dashboard).
 */
export default function AdminWarehousePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState("RECEIVED");
  const [bags, setBags] = useState([]);
  const [itemsByBag, setItemsByBag] = useState({});
  const [loading, setLoading] = useState(false);
  const [ticking, setTicking] = useState(false);
  const [tickResult, setTickResult] = useState(null);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    if (user && user.role !== "admin") { navigate("/"); }
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: bagsData }, itemsRes] = await Promise.all([
        api.get(`/rescue/admin/bags?status=${status}&limit=200`),
        api.get(`/rescue/admin/items?limit=500`).catch(() => ({ data: { items: [] } })),
      ]);
      setBags(bagsData.bags || []);
      const map = {};
      (itemsRes.data.items || []).forEach(it => { if (it.bag_id) map[it.bag_id] = it; });
      setItemsByBag(map);
    } catch { setBags([]); setItemsByBag({}); } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { if (user?.role === "admin") load(); }, [load, user]);

  const runLifecycleTick = async () => {
    setTicking(true);
    setTickResult(null);
    try {
      const { data } = await api.post(`/rescue/admin/lifecycle/tick`);
      setTickResult(data.transitions || {});
      load();
    } catch { setTickResult({ error: true }); } finally { setTicking(false); }
  };

  const renderBadge = (rs) => {
    const cfg = {
      PENDING: "bg-gray-100 text-gray-600",
      IN_PROGRESS: "bg-amber-50 text-amber-700",
      COMPLETED: "bg-green-50 text-green-700",
      FAILED: "bg-red-50 text-red-700",
    };
    const cls = cfg[rs] || "bg-gray-50 text-gray-500";
    return <span data-testid={`wh-render-${rs || "none"}`} className={`px-1.5 py-0.5 rounded text-[10px] ${cls}`}>{rs || "—"}</span>;
  };

  return (
    <div data-testid="admin-warehouse-page" className="min-h-screen bg-[#FAFAFA]">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5] bg-white">
        <button data-testid="warehouse-back" onClick={() => navigate("/admin")} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="text-base font-bold text-[#0A0A0A] flex items-center gap-2">
            <Boxes size={18} /> Warehouse · Bestand
          </h1>
          <p className="text-[10px] text-[#666]">Wareneingang läuft über <button onClick={() => navigate("/admin?section=hub_intake")} className="underline">Hub-Eingang</button>.</p>
        </div>
        <button
          data-testid="wh-lifecycle-tick"
          onClick={runLifecycleTick}
          disabled={ticking}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#E5E5E5] bg-white text-[#0A0A0A] rounded text-[11px] font-medium disabled:opacity-50">
          <RefreshCw size={12} className={ticking ? "animate-spin" : ""} /> Lifecycle-Tick
        </button>
      </div>

      {tickResult && !tickResult.error && (
        <div data-testid="wh-tick-result" className="mx-4 mt-3 text-[10px] text-[#666] bg-white border border-[#E5E5E5] px-3 py-1.5 rounded">
          Tick: {tickResult.promoted_to_rescue || 0} → RESCUE · {tickResult.disposed || 0} entsorgt · {tickResult.damaged_auto_disposed || 0} beschädigt auto-entsorgt
        </div>
      )}

      <div className="mx-4 my-4">
        <div className="flex gap-2 mb-3">
          {STATUS_TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`wh-status-${t.key}`}
                onClick={() => setStatus(t.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-medium border ${status === t.key ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] bg-white text-[#666]"}`}>
                <Icon size={12} /> {t.label}
              </button>
            );
          })}
        </div>

        {loading && <p className="text-[11px] text-[#999]">Lade …</p>}
        {!loading && bags.length === 0 && (
          <div data-testid="wh-empty" className="bg-white border border-[#E5E5E5] px-4 py-8 text-center text-[11px] text-[#999]">
            {status === "RECEIVED" ? "Aktuell keine Rescue-Bags im Warehouse." : status === "DAMAGED" ? "Keine beschädigten Bags." : "Keine entsorgten Bags."}
          </div>
        )}

        {!loading && bags.length > 0 && (
          <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-[#FAFAFA] text-[#999] text-left uppercase text-[10px]">
                <tr>
                  <th className="px-3 py-2 font-bold">Bag-ID</th>
                  <th className="px-3 py-2 font-bold">Kategorie</th>
                  <th className="px-3 py-2 font-bold">Tragedauer</th>
                  <th className="px-3 py-2 font-bold">Eingang</th>
                  <th className="px-3 py-2 font-bold">Init-Preis</th>
                  <th className="px-3 py-2 font-bold">Rescue-Preis</th>
                  <th className="px-3 py-2 font-bold">KI-Render</th>
                  <th className="px-3 py-2 font-bold">Item-Status</th>
                  <th className="px-3 py-2 font-bold">Bag-Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0F0F0]">
                {bags.map(b => {
                  const act = b.activation || {};
                  const it = itemsByBag[b.bag_id] || {};
                  return (
                    <tr key={b.bag_id} data-testid={`wh-row-${b.bag_id.slice(0,8)}`} className="hover:bg-[#FAFAFA]">
                      <td className="px-3 py-2 font-mono">{b.bag_id.slice(0,8).toUpperCase()}</td>
                      <td className="px-3 py-2">{act.category || "—"}</td>
                      <td className="px-3 py-2">{act.wear_duration_days ? `${act.wear_duration_days}T` : "—"}</td>
                      <td className="px-3 py-2 text-[#666]">{b.received_at ? b.received_at.slice(0,10) : "—"}</td>
                      <td className="px-3 py-2 font-mono">{it.initial_price != null ? `${Number(it.initial_price).toFixed(2)} €` : "—"}</td>
                      <td className="px-3 py-2 font-mono">{it.rescue_price != null ? `${Number(it.rescue_price).toFixed(2)} €` : "—"}</td>
                      <td className="px-3 py-2">{renderBadge(it.render_status)}</td>
                      <td className="px-3 py-2 text-[#666]">{it.status || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${b.status === "RECEIVED" ? "bg-green-50 text-green-700" : b.status === "DAMAGED" ? "bg-red-50 text-red-700" : "bg-[#F5F5F5] text-[#666]"}`}>
                          {b.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
